import { styled } from 'styled-components';

import { _useSelector } from '@src/state/hooks';
import { useState, useEffect } from 'react';

import GamesList from './GamesList';
import TournamentList from './TournamentList';
import FilterTourneyButton from '../FilterTourneyButton';
import RefreshGamesButton from '../RefreshGamesButton';
import { GameType } from '@src/lib/types';
import Stats from './Stats';
import { GameState, TournamentState } from '@src/state/slices/types';

const Container = styled.div`
  display: flex;
  align-items: stretch;
  flex-direction: column;
  flex: 1;

  margin: 10pt;
  margin-top: 2rem;
  margin-bottom: 7.5rem;

  border: 2pt solid gray;
  padding: 10pt;

  font-size: 32pt;

  // color hyperlink blue
  color: #0000ff;

  min-height: 0;

  //background color windows 98 gray
  background-color: #c0c0c0;

  @media only screen and (max-width: 1280px) {
    margin-bottom: 10pt;
    margin-top: 10pt;
  }
`;

const ListHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  align-content: center;
  justify-content: center;

  @media only screen and (max-width: 720px) {
    gap: 10pt;
    font-size: 2rem;
    flex-wrap: wrap;
  }
`;

const TabContainer = styled.div`
  width: 100%;

  display: flex;
  flex-direction: row;
  align-items: center;
  align-content: center;
  justify-content: space-around;

  @media only screen and (max-width: 720px) {
    gap: 10pt;
    font-size: 1.5rem;
    flex-wrap: wrap;
  }
`;

const Tab = styled.a<{ selected: boolean; glow?: boolean }>`
  text-decoration: ${(props) => (props.selected ? 'underline' : 'none')};
  cursor: pointer;

  ${(props) =>
    props.glow &&
    `
    text-shadow: -3px 0 5px green;
  `}

  -khtml-user-select: none;
  -o-user-select: none;
  -moz-user-select: none;
  -webkit-user-select: none;
  &:hover {
    text-decoration: underline;
  }
`;

type Props = {
  gameTypes: GameType[];
  onPressGameTypes: (gameTypes: GameType[]) => void;
  showJoinPrivateGamePanel: (
    gameId: string,
    gameName: string,
    gameWager: number,
  ) => void;
  showConfirmJoinGamePanel: (
    gameId: string,
    gameName: string,
    gameWager: number,
  ) => void;
};

export default function List(props: Props) {
  const [selectedTab, setSelectedTab] = useState<string>(
    props.gameTypes.includes('tournament') ? 'tournaments' : 'games',
  );
  const playerMeta = _useSelector((state) => state.game.playerMeta);
  const playerId = playerMeta?.playerId || '';
  const games: GameState['games'] =
    _useSelector((state) => state.game.games) || {};
  const tournaments: TournamentState['tournaments'] =
    _useSelector((state) => state.tournament.tournaments) || {};
  const [playerHasTourneyGames, setPlayerHasTourneyGames] = useState(false);
  const [filterTournaments, setFilterTournaments] = useState(false);

  useEffect(() => {
    if (games) {
      const filteredGames = Object.values(games)
        .filter((game) => !!game)
        .filter((game) => game.type === 'tournament')
        .filter((game) => !(game.terminated || game.gameEnded))
        .filter((game) => game.whitelist?.includes(playerId));

      setPlayerHasTourneyGames(filteredGames.length > 0);
    }
  }, [games]);

  const selectGames = () => {
    setSelectedTab('games');
    props.onPressGameTypes(['regular', 'wager']);
  };

  const selectTournaments = () => {
    setSelectedTab('tournaments');
    props.onPressGameTypes(['tournament']);
  };

  const onClickFilterTournaments = () => {
    setFilterTournaments(!filterTournaments);
  };

  return (
    <Container>
      <ListHeader>
        <TabContainer>
          <RefreshGamesButton />
          {selectedTab === 'tournaments' && (
            <FilterTourneyButton
              onClick={onClickFilterTournaments}
              filtered={filterTournaments}
            />
          )}
          <Tab onClick={selectGames} selected={selectedTab === 'games'}>
            Games
          </Tab>
          <Tab
            onClick={selectTournaments}
            selected={selectedTab === 'tournaments'}
            glow={playerHasTourneyGames || Object.keys(tournaments).length > 0}
          >
            Tournaments
          </Tab>
        </TabContainer>
      </ListHeader>
      {selectedTab === 'tournaments' && (
        <TournamentList
          games={games}
          tournaments={tournaments}
          gameTypes={props.gameTypes}
          filterTournamentGames={filterTournaments}
        />
      )}
      {selectedTab === 'games' && (
        <GamesList
          games={games}
          gameTypes={props.gameTypes}
          showJoinPrivateGamePanel={props.showJoinPrivateGamePanel}
          showConfirmJoinGamePanel={props.showConfirmJoinGamePanel}
        />
      )}

      <Stats />
    </Container>
  );
}
